import React, { useState, useEffect, useRef } from 'react';
import api, { getSchoolAssetUrl } from '../utils/api';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Card, CardContent } from './ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from './ui/dialog';
import { Plus, School, Edit, MapPin, Phone, Mail, Image } from 'lucide-react';
import { useToast } from '../hooks/use-toast';

const Schools = () => {
  const [schools, setSchools] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const { toast } = useToast();

  const [formData, setFormData] = useState({
    name: '', code: '', address: '', city: '', state: '', country: '', pincode: '',
    phone: '', email: '', website: '', logo: '', admin_email: '', admin_first_name: '', admin_last_name: '',
  });
  const [editingSchool, setEditingSchool] = useState(null);
  const [logoUploading, setLogoUploading] = useState(false);
  const logoInputRef = useRef(null);

  useEffect(() => { fetchSchools(); }, []);

  const handleLogoPick = (e) => {
    const file = e.target.files?.[0];
    if (!file || !editingSchool?.id) return;
    if (!file.type.startsWith('image/')) {
      toast({ title: 'Invalid file', description: 'Choose an image (JPG, PNG, GIF, WebP)', variant: 'destructive' });
      return;
    }
    setLogoUploading(true);
    const formData = new FormData();
    formData.append('logo', file);
    api.post(`schools/${editingSchool.id}/logo`, formData, { headers: { 'Content-Type': 'multipart/form-data' } })
      .then((res) => {
        const path = res.data?.data?.logo;
        if (path) setFormData((prev) => ({ ...prev, logo: path }));
        toast({ title: 'Success', description: 'Logo uploaded' });
      })
      .catch((err) => toast({ title: 'Error', description: err.response?.data?.message || 'Upload failed', variant: 'destructive' }))
      .finally(() => { setLogoUploading(false); if (logoInputRef.current) logoInputRef.current.value = ''; });
  };

  const fetchSchools = async () => {
    try {
      const response = await api.get('/schools');
      setSchools(response.data.data || []);
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to fetch schools', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingSchool) {
        await api.put(`schools/${editingSchool.id}`, formData);
        toast({ title: 'Success', description: 'School updated.' });
      } else {
        await api.post('/schools', formData);
        toast({ title: 'Success', description: 'School created. Admin credentials sent via email.' });
      }
      setIsDialogOpen(false);
      setEditingSchool(null);
      setFormData({ name: '', code: '', address: '', city: '', state: '', country: '', pincode: '',
        phone: '', email: '', website: '', logo: '', admin_email: '', admin_first_name: '', admin_last_name: '' });
      fetchSchools();
    } catch (error) {
      toast({ title: 'Error', description: error.response?.data?.message || 'Failed', variant: 'destructive' });
    }
  };

  const handleEdit = (school) => {
    setEditingSchool(school);
    setFormData({
      name: school.name || '', code: school.code || '', address: school.address || '', city: school.city || '',
      state: school.state || '', country: school.country || '', pincode: school.pincode || '',
      phone: school.phone || '', email: school.email || '', website: school.website || '', logo: school.logo || '',
      admin_email: '', admin_first_name: '', admin_last_name: '',
    });
    setIsDialogOpen(true);
  };

  return (
    <div className="space-y-6" data-testid="schools-page">
      <div className="flex flex-col sm:flex-row justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-white">Schools</h1>
          <p className="text-slate-400">Manage schools in the system</p>
        </div>
        <Dialog open={isDialogOpen} onOpenChange={(open) => { if (!open) setEditingSchool(null); setIsDialogOpen(open); }}>
          <DialogTrigger asChild>
            <Button className="bg-blue-600 hover:bg-blue-700" data-testid="add-school-btn">
              <Plus className="w-4 h-4 mr-2" /> Add School
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-slate-800 border-slate-700 text-white max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader><DialogTitle>{editingSchool ? 'Edit School' : 'Add New School'}</DialogTitle></DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="p-3 bg-slate-700/50 rounded-lg">
                <h4 className="font-medium text-white mb-3">School Information</h4>
                <div className="grid grid-cols-2 gap-4">
                  <div><Label>School Name *</Label><Input value={formData.name} onChange={(e) => setFormData({...formData, name: e.target.value})} className="bg-slate-700 border-slate-600" required /></div>
                  <div><Label>Code</Label><Input value={formData.code} onChange={(e) => setFormData({...formData, code: e.target.value})} className="bg-slate-700 border-slate-600" /></div>
                </div>
                <div className="mt-4"><Label>Address</Label><Input value={formData.address} onChange={(e) => setFormData({...formData, address: e.target.value})} className="bg-slate-700 border-slate-600" /></div>
                <div className="grid grid-cols-2 gap-4 mt-4">
                  <div><Label>City</Label><Input value={formData.city} onChange={(e) => setFormData({...formData, city: e.target.value})} className="bg-slate-700 border-slate-600" /></div>
                  <div><Label>State</Label><Input value={formData.state} onChange={(e) => setFormData({...formData, state: e.target.value})} className="bg-slate-700 border-slate-600" /></div>
                </div>
                <div className="grid grid-cols-2 gap-4 mt-4">
                  <div><Label>Phone</Label><Input value={formData.phone} onChange={(e) => setFormData({...formData, phone: e.target.value})} className="bg-slate-700 border-slate-600" /></div>
                  <div><Label>Email</Label><Input type="email" value={formData.email} onChange={(e) => setFormData({...formData, email: e.target.value})} className="bg-slate-700 border-slate-600" /></div>
                </div>
                <div className="mt-4">
                  <Label className="flex items-center gap-2"><Image className="w-4 h-4" /> School Logo</Label>
                  <div className="flex items-center gap-3 mt-2 flex-wrap">
                    <input ref={logoInputRef} type="file" accept="image/jpeg,image/png,image/gif,image/webp" onChange={handleLogoPick} className="hidden" />
                    {editingSchool?.id && (
                      <Button type="button" variant="outline" onClick={() => logoInputRef.current?.click()} disabled={logoUploading} className="bg-slate-700 border-slate-600 text-white hover:bg-slate-600">
                        {logoUploading ? 'Uploading...' : 'Choose image'}
                      </Button>
                    )}
                    {formData.logo && (
                      <img src={getSchoolAssetUrl(formData.logo, editingSchool?.id) || ''} alt="Logo" className="h-12 w-12 object-contain rounded border border-slate-600 bg-slate-700" onError={(e) => { e.target.style.display = 'none'; }} />
                    )}
                  </div>
                  <p className="text-xs text-slate-500 mt-1">When editing: pick image to upload to assets/school/{editingSchool?.id || '…'}/. Used on fee receipts.</p>
                </div>
              </div>

              <div className="p-3 bg-slate-700/50 rounded-lg">
                <h4 className="font-medium text-white mb-3">School Admin (Optional)</h4>
                <div className="grid grid-cols-2 gap-4">
                  <div><Label>Admin First Name</Label><Input value={formData.admin_first_name} onChange={(e) => setFormData({...formData, admin_first_name: e.target.value})} className="bg-slate-700 border-slate-600" /></div>
                  <div><Label>Admin Last Name</Label><Input value={formData.admin_last_name} onChange={(e) => setFormData({...formData, admin_last_name: e.target.value})} className="bg-slate-700 border-slate-600" /></div>
                </div>
                <div className="mt-4"><Label>Admin Email</Label><Input type="email" value={formData.admin_email} onChange={(e) => setFormData({...formData, admin_email: e.target.value})} className="bg-slate-700 border-slate-600" placeholder="Credentials will be sent to this email" /></div>
              </div>

              <Button type="submit" className="w-full bg-blue-600 hover:bg-blue-700">{editingSchool ? 'Update School' : 'Create School'}</Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {loading ? (
        <div className="flex justify-center py-12"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div></div>
      ) : schools.length === 0 ? (
        <Card className="bg-slate-800 border-slate-700"><CardContent className="py-12 text-center">
          <School className="w-12 h-12 text-slate-500 mx-auto mb-4" /><p className="text-slate-400">No schools found</p>
        </CardContent></Card>
      ) : (
        <div className="grid gap-4 md:grid-cols-2">
          {schools.map(school => {
            const logoUrl = getSchoolAssetUrl(school.logo, school.id);
            return (
            <Card key={school.id} className="bg-slate-800 border-slate-700 hover:border-slate-600">
              <CardContent className="p-6">
                <div className="flex items-start gap-4">
                  <div className="w-14 h-14 bg-blue-500/20 rounded-xl flex items-center justify-center flex-shrink-0 overflow-hidden">
                    {logoUrl ? (
                      <img src={logoUrl} alt="" className="w-full h-full object-contain" onError={(e) => { e.target.style.display = 'none'; e.target.nextElementSibling?.classList.remove('hidden'); }} />
                    ) : null}
                    <div className={`w-full h-full flex items-center justify-center ${logoUrl ? 'hidden' : ''}`}>
                      <School className="w-7 h-7 text-blue-500" />
                    </div>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-2">
                      <h3 className="font-semibold text-white text-lg truncate">{school.name}</h3>
                      <Button variant="ghost" size="icon" onClick={() => handleEdit(school)} className="text-slate-400 hover:text-white shrink-0">
                        <Edit className="w-4 h-4" />
                      </Button>
                      <span className={`px-2 py-1 rounded-full text-xs shrink-0 ${school.status === 'active' ? 'bg-green-500/20 text-green-400' : 'bg-slate-500/20 text-slate-400'}`}>
                        {school.status}
                      </span>
                    </div>
                    {school.code && <p className="text-sm text-slate-400">Code: {school.code}</p>}
                    {school.address && (
                      <div className="flex items-center gap-1 mt-2 text-sm text-slate-500">
                        <MapPin className="w-3 h-3" />{school.address}, {school.city}
                      </div>
                    )}
                    <div className="flex items-center gap-4 mt-2">
                      {school.phone && <span className="flex items-center gap-1 text-xs text-slate-500"><Phone className="w-3 h-3" />{school.phone}</span>}
                      {school.email && <span className="flex items-center gap-1 text-xs text-slate-500"><Mail className="w-3 h-3" />{school.email}</span>}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          ); })}
        </div>
      )}
    </div>
  );
};

export default Schools;
